// The station's own tank chart (rows of [inches, gallons]) replaces the size-based estimate when present.
const KEY = "fhp-charts";
let cache = (() => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } })();

export const chartKey = (siteId, label) => `${siteId}|${label ?? ""}`;
export const getChart = (siteId, label) => cache[chartKey(siteId, label)] || null;

export function cacheCharts(sites) {
  const next = {};
  for (const s of sites) for (const t of s.tanks || []) if (t.chart?.length) next[chartKey(s.id, t.label)] = t.chart;
  cache = next;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage full */ }
  window.dispatchEvent(new Event("charts-changed"));
}

// Straight line between the two chart rows around the stick.
export function chartGallons(rows, inches) {
  if (inches <= rows[0][0]) return { gal: rows[0][1] * (rows[0][0] ? inches / rows[0][0] : 1), lo: rows[0], hi: rows[0] };
  for (let i = 1; i < rows.length; i++) {
    const [a, b] = [rows[i - 1], rows[i]];
    if (inches <= b[0]) return { gal: a[1] + ((inches - a[0]) / (b[0] - a[0])) * (b[1] - a[1]), lo: a, hi: b };
  }
  const last = rows[rows.length - 1];
  return { gal: last[1], lo: last, hi: last, beyond: inches > last[0] };
}

export function chartInches(rows, gal) {
  if (gal <= rows[0][1]) return rows[0][1] ? (gal / rows[0][1]) * rows[0][0] : rows[0][0];
  for (let i = 1; i < rows.length; i++) {
    const [a, b] = [rows[i - 1], rows[i]];
    if (gal <= b[1]) return b[1] === a[1] ? a[0] : a[0] + ((gal - a[1]) / (b[1] - a[1])) * (b[0] - a[0]);
  }
  return rows[rows.length - 1][0];
}

export function projectDropChart(startInches, dropGallons, rows, cap) {
  const at = chartGallons(rows, startInches);
  const startGallons = Math.round(at.gal);
  const top = rows[rows.length - 1][1];
  const capacity = cap || top;
  const finalGallons = startGallons + Math.max(0, dropGallons);
  const finalInches = chartInches(rows, Math.min(finalGallons, top));
  const startGpi = at.hi[0] > at.lo[0] ? (at.hi[1] - at.lo[1]) / (at.hi[0] - at.lo[0]) : 0;
  const pct = (g) => (capacity > 0 ? (g / capacity) * 100 : 0);
  const cap90 = Math.round(capacity * 0.9), cap95 = Math.round(capacity * 0.95);
  const finalPct = pct(finalGallons);
  return {
    startGallons, startPct: pct(startGallons), startGpi, finalGallons, finalInches, finalPct,
    stickGain: finalInches - startInches, cap90, cap95,
    ullage90: cap90 - finalGallons, ullage95: cap95 - finalGallons,
    startUllage90: Math.max(0, cap90 - startGallons),
    status: finalPct > 95 ? "critical" : finalPct > 90 ? "caution" : "safe",
    h90: chartInches(rows, cap90), h95: chartInches(rows, cap95),
    source: "chart", chartRows: [at.lo, at.hi], beyondStart: !!at.beyond, beyondChart: !!at.beyond || finalGallons > top,
  };
}

const NUM = /\d+(?:\.\d+)?(?:[\s-]+\d{1,2}\/\d{1,2})?/g;
const toNum = (x) => {
  const m = x.match(/^(\d+(?:\.\d+)?)(?:[\s-]+(\d{1,2})\/(\d{1,2}))?$/);
  return m ? +m[1] + (m[2] ? +m[2] / +m[3] : 0) : NaN;
};

// One line per row: "inches gallons" (tabs, spaces, CSV, 1/16 fractions, 1,250-style gallons). Side-by-side column groups OK.
export function parseChartText(text) {
  const rows = [];
  let bad = 0;
  for (const raw of String(text).split(/\n/)) {
    const line = raw.replace(/\b(inches|inch|in|gallons|gals|gal)\b|["']/gi, " ").replace(/,\s+/g, "\t").trim();
    if (!/\d/.test(line) || /[a-z]/i.test(line)) continue;
    const spaced = /\d\s+\d|\t/.test(line.replace(/\s+\d{1,2}\/\d{1,2}/g, ""));
    const clean = spaced ? line.replace(/,(?=\d{3}\b)/g, "") : line.replace(/[,;]/g, " ");
    const nums = (clean.match(NUM) || []).map((x) => toNum(x.trim()));
    if (nums.length < 2 || nums.length % 2 || nums.some((n) => !Number.isFinite(n))) { bad += 1; continue; }
    for (let k = 0; k < nums.length; k += 2) rows.push([nums[k], nums[k + 1]]);
  }
  rows.sort((x, y) => x[0] - y[0]);
  const ok = !bad && rows.length >= 2 && rows.every((r, i) => r[0] <= 240 && r[1] <= 80000 && (i === 0 || (r[0] > rows[i - 1][0] && r[1] >= rows[i - 1][1])));
  return { rows, ok, bad };
}
